/**
 * パントリー商品カート追加のビジネスロジック（純粋関数）
 *
 * DOM操作・副作用を含まない。Vitestでテスト可能。
 * v1 互換: assets/product-form.js:632-794 ProductPantryCartButtonWrap の createBody / donationMoney。
 *
 * @see docs/business-rules/pantry.md - 寄付金は総額の 1%（floor）
 */
import type { SelectedPantryEntry } from './pantry-products.logic';

/** Cart Ajax API へ送る 1 アイテム分の最小構造 */
export interface CartAddItem {
  id: number;
  quantity: number;
  properties: Record<string, string>;
}

/** /cart/add.js への bulk add リクエスト body */
export interface PantryCartAddPayload {
  items: CartAddItem[];
  sections_url: string;
}

/** Cart Ajax API のエラーレスポンス形（必要部分のみ） */
export interface PantryCartApiErrorResponse {
  status?: number;
  description?: string;
  message?: string;
}

/** v1 と同じ properties キー（内容量は K9/CO-04 で追加） */
export const PROPERTY_KEYS = {
  SUPPORT_GROUP: '支援先団体',
  SUPPORT_AMOUNT: '支援金額',
  PANTRY_KEY: '_pantryKey',
  PANTRY_TYPE: '_パントリータイプ',
  EXPIRATION: '賞味期限',
  VOLUME: '内容量',
} as const;

/** v1 と同じパントリータイプ値 */
export const PANTRY_TYPE_VALUES = {
  CASE: 'パントリーケース',
  PIECE: 'パントリーピース',
} as const;

/**
 * 親子紐付けユニークキーを生成する。
 *
 * v1 互換: assets/product-form.js:657-659 `Date.now().toString(36)`。
 * sections/main-cart-items.liquid が _pantryKey を見て親子集約表示するため、
 * 同一カートに同じ親パントリーが複数入る可能性に備えてユニーク性が必要。
 */
export function createPantryKey(): string {
  return Date.now().toString(36);
}

/**
 * 選択エントリの合計金額を計算する。
 */
export function calcPantryGrandTotal(entries: readonly SelectedPantryEntry[]): number {
  let total = 0;
  for (const entry of entries) {
    total += entry.variantPrice * entry.quantity;
  }
  return total;
}

/**
 * パントリー商品の寄付金額を計算する（v1 `pantryProducts.donationMoney()` 互換）。
 *
 * v1: assets/product-form.js の donationMoney = `Math.floor(grandTotalPrice * 0.01)`
 *
 * @see docs/business-rules/pantry.md - 「パントリー商品の支援金額は総額の 1% で計算されます」
 */
export function calcPantryDonationMoney(grandTotal: number): number {
  return Math.floor(grandTotal * 0.01);
}

/**
 * 親 + 子の items 配列を組み立てる。
 *
 * v1 互換: assets/product-form.js:759-790 createBody。
 * - 親（パントリーケース）は quantity: 1 固定で、支援先団体 / 支援金額 / _pantryKey を持つ
 * - 子（パントリーピース）は各エントリの quantity をそのまま使い、_pantryKey / 賞味期限 / 内容量 を持つ
 *   （内容量は product 単位で variant 不変。Checkout 明細表示用、空なら付与しない＝CO-04）
 * - 親子で同じ _pantryKey を共有することで、カート画面の親子集約表示が成立する
 */
export function buildPantryCartItems(input: {
  parentVariantId: number;
  entries: readonly SelectedPantryEntry[];
  pantryKey: string;
  supportGroup: string;
  donationAmount: number;
}): CartAddItem[] {
  const parent: CartAddItem = {
    id: input.parentVariantId,
    quantity: 1,
    properties: {
      [PROPERTY_KEYS.SUPPORT_GROUP]: input.supportGroup,
      [PROPERTY_KEYS.SUPPORT_AMOUNT]: input.donationAmount.toString(),
      [PROPERTY_KEYS.PANTRY_KEY]: input.pantryKey,
      [PROPERTY_KEYS.PANTRY_TYPE]: PANTRY_TYPE_VALUES.CASE,
    },
  };
  const children: CartAddItem[] = input.entries.map((entry) => {
    const properties: Record<string, string> = {
      [PROPERTY_KEYS.PANTRY_KEY]: input.pantryKey,
      [PROPERTY_KEYS.PANTRY_TYPE]: PANTRY_TYPE_VALUES.PIECE,
      [PROPERTY_KEYS.EXPIRATION]: entry.expirationOn,
    };
    // 内容量（CO-04）。空（volume 未設定）のときは「内容量: 」の空行を出さない。
    if (entry.volume) {
      properties[PROPERTY_KEYS.VOLUME] = entry.volume;
    }
    return {
      id: Number(entry.variantId),
      quantity: entry.quantity,
      properties,
    };
  });
  return [parent, ...children];
}

/** /cart/add.js への bulk add リクエスト body を組み立てる */
export function buildPantryAddPayload(input: {
  items: readonly CartAddItem[];
  sectionsUrl: string;
}): PantryCartAddPayload {
  return {
    items: [...input.items],
    sections_url: input.sectionsUrl,
  };
}

/**
 * Cart Ajax API のエラーレスポンスからユーザー表示用メッセージを抽出する。
 *
 * v1 互換: description を優先（assets/product-form.js:304-306 と同じ）。
 */
export function parsePantryCartError(
  response: PantryCartApiErrorResponse,
  fallback: string,
): string {
  return response.description || response.message || fallback;
}
